import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Briefcase, Building2, CalendarPlus, Inbox, Plus } from "lucide-react";
import SpeedDial from "../components/ui/SpeedDial";
import { useLanguage } from "../components/ui/LanguageProvider";

/**
 * 폰 셸의 파란 둥근 버튼(FAB) — 한 화면에 하나만(파란 면 하나 · 폰 레스토프). 데모: docs/MOBILE_WEBAPP.md §7.
 *  - 홈(/crm/dashboard): 스피드 다이얼(리드·기회·일정·회사 추가). 고르면 그 화면으로 가며 state { quickCreate: true }.
 *  - 다른 화면: 본문에 `[data-primary-action]`(헤더 파란 "추가")이 있으면 확장형 FAB 이 그 버튼을 대신 누른다.
 *    헤더 원래 버튼은 셸 CSS 가 숨긴다(숨긴 버튼도 .click() 은 된다). 내리면 원으로 접히고 올리면 펼친다.
 *  - 화면이 스스로 SpeedDial 을 쓰거나 `[data-no-primary-fab]` 를 두면 셸 FAB 을 띄우지 않는다(헤더 버튼도 그대로).
 *  - 더보기(/crm/more)엔 없다. 키보드가 열리면 셸 CSS 가 숨긴다(html.kb-open).
 *  - quickCreate: 어느 화면이든 state 를 보면 본문의 `[data-primary-action]` 을 찾아(최대 4초) 한 번 누르고 state 를 비운다.
 */
export type FabMode = "dial" | "proxy" | null;

type Target = { el: HTMLElement; label: string; disabled: boolean };

const sameTarget = (a: Target | null, b: Target | null) =>
  a === b || (!!a && !!b && a.el === b.el && a.label === b.label && a.disabled === b.disabled);

export default function MobileFab({
  mainRef,
  onModeChange,
}: {
  mainRef: React.RefObject<HTMLElement>;
  onModeChange: (mode: FabMode) => void;
}) {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname, search } = location;
  const quickCreate = !!(location.state as { quickCreate?: boolean } | null)?.quickCreate;
  const [target, setTarget] = React.useState<Target | null>(null);
  const [blocked, setBlocked] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(false);
  const tRef = React.useRef(t);
  tRef.current = t;

  // 본문 DOM 감시 — 주요 버튼이 생기고·사라지고·글자가 바뀌는 것을 따라간다(120ms 디바운스).
  React.useEffect(() => {
    const main = mainRef.current;
    if (!main) return undefined;
    let timer = 0;
    const scan = () => {
      setBlocked(!!main.querySelector("[data-no-primary-fab], [data-speed-dial]"));
      const el = main.querySelector<HTMLElement>("[data-primary-action]");
      const next: Target | null = el
        ? {
            el,
            label: (el.textContent || "").replace(/\s+/g, " ").trim() || el.getAttribute("aria-label") || tRef.current("추가", "Add"),
            disabled: el.matches(":disabled") || el.getAttribute("aria-disabled") === "true",
          }
        : null;
      setTarget((prev) => (sameTarget(prev, next) ? prev : next));
    };
    const schedule = () => { window.clearTimeout(timer); timer = window.setTimeout(scan, 120); };
    scan();
    const mo = new MutationObserver(schedule);
    mo.observe(main, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["disabled", "aria-disabled", "data-primary-action", "data-no-primary-fab", "data-speed-dial"],
    });
    return () => { window.clearTimeout(timer); mo.disconnect(); };
  }, [mainRef, pathname]);

  // 스크롤 방향 — 내리면 접고(40px 이후), 올리면 편다. 화면이 바뀌면 펼친 상태로.
  React.useEffect(() => {
    const main = mainRef.current;
    setCollapsed(false);
    if (!main) return undefined;
    let last = main.scrollTop;
    const onScroll = () => {
      const st = main.scrollTop;
      if (st > last + 6 && st > 40) setCollapsed(true);
      else if (st < last - 6) setCollapsed(false);
      if (Math.abs(st - last) > 6) last = st;
    };
    main.addEventListener("scroll", onScroll, { passive: true });
    return () => main.removeEventListener("scroll", onScroll);
  }, [mainRef, pathname]);

  // 빠른 추가 — 도착한 화면의 주요 버튼을 한 번 누르고 state 를 비운다(뒤로가기·새로고침 때 다시 열리지 않게).
  React.useEffect(() => {
    if (!quickCreate) return undefined;
    const path = pathname + search;
    const clear = () => {
      // 버튼이 다른 화면으로 보냈으면(예: /new) 그대로 둔다
      if (window.location.pathname + window.location.search === path) navigate(path, { replace: true, state: null });
    };
    const start = Date.now();
    const poll = window.setInterval(() => {
      // 화면이 스스로 빠른 추가를 처리하고 표시를 지웠으면(기회·일정 화면 — ⌘K 용) 누르지 않는다. 두 번 누르면 토글형 버튼이 도로 닫힌다.
      const live = (window.history.state as { usr?: { quickCreate?: boolean } } | null)?.usr?.quickCreate;
      if (!live) { window.clearInterval(poll); return; }
      const el = mainRef.current?.querySelector<HTMLElement>("[data-primary-action]:not([disabled])");
      if (el) {
        window.clearInterval(poll);
        el.click();
        clear();
      } else if (Date.now() - start >= 4000) {
        window.clearInterval(poll);
        clear();
      }
    }, 150);
    return () => window.clearInterval(poll);
  }, [quickCreate, pathname, search, navigate, mainRef]);

  const isHome = pathname === "/crm/dashboard";
  const mode: FabMode = pathname === "/crm/more" || blocked ? null : isHome ? "dial" : target ? "proxy" : null;
  React.useEffect(() => { onModeChange(mode); }, [mode, onModeChange]);
  React.useEffect(() => () => onModeChange(null), [onModeChange]);

  if (mode === "dial") {
    const go = (to: string) => () => navigate(to, { state: { quickCreate: true } });
    return (
      <SpeedDial
        label={t("빠른 추가", "Quick add")}
        items={[
          { key: "lead", label: t("리드 추가", "Add lead"), icon: <Inbox size={18} aria-hidden />, onSelect: go("/crm/leads") },
          { key: "opp", label: t("기회 추가", "Add deal"), icon: <Briefcase size={18} aria-hidden />, onSelect: go("/crm/opportunities") },
          { key: "event", label: t("일정 추가", "Add event"), icon: <CalendarPlus size={18} aria-hidden />, onSelect: go("/crm/calendar") },
          { key: "company", label: t("회사 추가", "Add company"), icon: <Building2 size={18} aria-hidden />, onSelect: go("/crm/relationships") },
        ]}
      />
    );
  }

  if (mode === "proxy" && target) {
    return (
      <button
        type="button"
        data-mobile-fab=""
        aria-label={target.label}
        disabled={target.disabled}
        onClick={() => target.el.click()}
        className="mobile-fab-enter fixed right-4 inline-flex h-[52px] min-w-[52px] items-center justify-center rounded-[16px] border-0 bg-primary px-[15px] text-ink-inverse disabled:opacity-50"
        style={{ zIndex: 1030, bottom: "calc(56px + env(safe-area-inset-bottom) + 16px)", boxShadow: "0 6px 16px rgba(0,91,246,.35)" }}
      >
        <Plus size={22} aria-hidden className="flex-shrink-0" />
        <span
          aria-hidden
          data-crm-i18n-skip="true"
          className={`overflow-hidden whitespace-nowrap text-[15px] font-semibold transition-all duration-base ease-out motion-reduce:transition-none ${collapsed ? "ml-0 mr-0 max-w-0 opacity-0" : "ml-2 mr-1 max-w-[200px] opacity-100"}`}
        >
          {target.label}
        </span>
      </button>
    );
  }
  return null;
}
